import { Button, Col, Input, Row, Space } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useFetchEntity } from "~Features/Team/apiHooks/useFetchEntity.ts";
import { StyledColorPicker, StyledFormInputs, StyledFormSelect } from "~components/Form/StyledFormInputs.tsx";
import { SelectEntityGroup } from "~components/SelectEntityGroup/SelectEntityGroup.tsx";
import { useCreateTeam, useUpdateTeam } from "~entities/api";
import { useFetchVenueById } from "~entities/api/useVenuesAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { UploadImageForm } from "~components/Images";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import OldInfinitySelect from "../../components/SelectFilter/OldInfinitySelect";
import usePermissionsByCompetitionName from "../../hooks/usePermissionsByCompetitionName";
import HistoricNames from "./HistoricNames";
import { ageGroup, gender, statuses } from "./constants";
import { ImageTypes } from "~enums/imageTypes";
import { ImageBaseTypes } from "~enums/imageBaseTypes";

interface IFormInput {
  nameFullLocal: string;
  codeLocal: string | null;
  nameFullLatin: string | null;
  codeLatin: string | null;
  status: string;
  internationalReference: string | null;
  gender: string;
  ageGroup: string;
  standard: string;
  defaultVenueId: string | null;
  entityGroupId: string | null;
  representing: string | null;
  discipline: string;
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  social: {
    website: string | null;
    facebook: string | null;
    twitter: string | null;
    instagram: string | null;
  };
}

const ManageTeam = () => {
  const { sport, organizationId, customerId, entityId } = useParams();
  const sportConfiguration = useSportConfiguration();
  const navigate = useNavigate();
  const { state } = useLocation();
  const returnUrl = state?.from || `/${customerId}/admin/${sport}/${organizationId}/entities`;
  const { entityPermissionName: PermissionName } = usePermissionsByCompetitionName();

  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();

  const { mutateAsync: createFn, isPending: isCreatePending } = useCreateTeam();
  const { mutate: updateFn, isPending: isUpdatePending } = useUpdateTeam();

  const { data, isLoading: isFetchLoading, refetch: refetchEntity } = useFetchEntity({ entityId });

  const { data: venue, refetch } = useFetchVenueById(data?.defaultVenueId);
  const { entityStandards: STANDARDS } = useSportConfiguration();

  useEffect(() => {
    if (data?.defaultVenueId) {
      refetch();
    }
  }, [data, refetch]);

  useEffect(() => {
    if (entityId) {
      reset({
        nameFullLocal: data?.nameFullLocal,
        codeLocal: data?.codeLocal,
        nameFullLatin: data?.nameFullLatin,
        codeLatin: data?.codeLatin,
        status: data?.status,
        internationalReference: data?.internationalReference,
        gender: data?.gender,
        ageGroup: data?.ageGroup,
        standard: data?.standard,
        discipline: data?.discipline,
        defaultVenueId: data?.defaultVenueId,
        entityGroupId: data?.entityGroupId,
        representing: data?.representing,
        colors: {
          primary: data?.colors.primary || "000000",
          secondary: data?.colors.secondary || "000000",
          tertiary: data?.colors.tertiary || "000000",
        },
        social: {
          website: data?.social?.website,
          facebook: data?.social?.facebook,
          twitter: data?.social?.twitter,
          instagram: data?.social?.instagram,
        },
      });
    } else {
      reset({
        colors: {
          primary: "000000",
          secondary: "000000",
          tertiary: "000000",
        },
      });
    }
  }, [data, entityId, reset]);

  const title = useMemo(() => {
    return entityId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data?.nameFullLocal ? `- ${data?.nameFullLocal}` : ""}`
      : `${formatMessage({
          id: "addTeam",
          defaultMessage: "Adding a New Team",
        })}`;
  }, [data?.nameFullLocal, entityId, formatMessage]);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    if (entityId) {
      // @ts-ignore
      updateFn(data);
    } else {
      // @ts-ignore
      createFn(data).then(() => navigate(returnUrl, { state: { entityName: data.nameFullLocal, status: data.status }}));
    }
  };

  // @ts-ignore
  return (
    <Page title={title} tabTitle={title}>
      <LoaderContainer loading={isUpdatePending || isCreatePending || isFetchLoading}>
        <Content>
          <form style={{ marginLeft: 20, width: 960 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local.full"
                      name="nameFullLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "team",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "name.full.local",
                            }),
                          },
                        ),
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="name.local.code"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.code.item",
                        },
                        {
                          item: formatMessage({
                            id: "team",
                          }),
                        },
                      )}
                      name="codeLocal"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <Row>
                  <FormControl
                    labelText="gender"
                    name="gender"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.gender",
                    })}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={gender.map((gender) => ({
                          value: gender,
                          label: gender,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="standard"
                    name="standard"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.standard",
                    })}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={STANDARDS.map((st) => ({
                          value: st,
                          label: st,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="ageGroup"
                    name="ageGroup"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.age.group",
                    })}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={ageGroup.map((age) => ({
                          value: age,
                          label: age,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="status"
                    name="status"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.status",
                    })}
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "status",
                          }),
                        },
                      ),
                    }}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={statuses.map((status) => ({
                          value: status,
                          label: status,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                </Row>
                <Row>
                  <FormControl
                    labelText="representing"
                    name="representing"
                    labelTooltipId="representing"
                    control={control}
                    labelTooltip={formatMessage({ id: "form.help.team.representing" })}
                    render={({ field }) => <StyledFormInputs {...field} />}
                  />
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin.full"
                      name="nameFullLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.full.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "team",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="name.latin.code"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.code.item",
                        },
                        {
                          item: formatMessage({
                            id: "team",
                          }),
                        },
                      )}
                      name="codeLatin"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <StyledFormInputs {...field} />
                          {fieldState.error && (
                            <div role="alert" style={{ color: "red" }}>
                              {formatMessage({ id: "form.validation.max.length" }, { length: 5 })}
                            </div>
                          )}
                        </>
                      )}
                      rules={{
                        maxLength: {
                          value: 5,
                          message: formatMessage({ id: "form.validation.max.length" }, { length: 5 }),
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <FormControl
                    labelText="internationalReference"
                    name="internationalReference"
                    control={control}
                    labelTooltip={formatMessage(
                      {
                        id: "form.help.international.reference.item",
                      },
                      {
                        item: formatMessage({
                          id: "team",
                        }),
                      },
                    )}
                    render={({ field }) => <StyledFormInputs {...field} />}
                  />
                  <FormControl
                    labelText="club"
                    name="entityGroupId"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.club",
                    })}
                    render={({ field }) => {
                      return (
                        <SelectEntityGroup
                          allowClear
                          value={
                            field?.value === data?.entityGroup?.entityGroupId
                              ? {
                                  value: data?.entityGroup?.entityGroupId,
                                  label: data?.entityGroup?.nameFullLocal,
                                }
                              : field.value
                          }
                          onChange={(option) => {
                            field.onChange(option?.value || "");
                          }}
                        />
                      );
                    }}
                    errors={errors}
                  />

                  <FormControl
                    labelText="venue.default"
                    name="defaultVenueId"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.team.venue",
                    })}
                    render={({ field }) => (
                      <OldInfinitySelect
                        style={{ width: "100%" }}
                        handleSelect={(data: any) => {
                          let eventValue: string;
                          if (typeof data !== "string" && !Array.isArray(data)) {
                            eventValue = data.value;
                            const event = {
                              target: {
                                name: field.name,
                                value: eventValue,
                              },
                            };
                            field.onChange(event);
                          }
                        }}
                        handleClear={() => {
                          const event = {
                            target: {
                              name: field.name,
                              value: null,
                            },
                          };
                          field.onChange(event);
                        }}
                        URL={`${sport}/o/${organizationId}/venues`}
                        searchParam="nameLocalContains"
                        placeholder="All Venues"
                        parseFunction={(item: any) => ({
                          label: item.nameLocal,
                          value: item.venueId,
                        })}
                        permissionName={PermissionNames.VENUE}
                        defaultValue={{ value: field.value, label: venue?.nameLocal }}
                      />
                    )}
                    errors={errors}
                  />

                  {sportConfiguration.disciplines?.length ? (
                    <FormControl
                      labelText="discipline"
                      name="discipline"
                      control={control}
                      labelTooltip={formatMessage({
                        id: "form.help.team.discipline",
                      })}
                      render={({ field }) => (
                        <StyledFormSelect
                          style={{ width: "100%" }}
                          {...field}
                          options={[
                            { value: null, label: "" },
                            ...sportConfiguration.disciplines.map((discipline) => ({
                              value: discipline,
                              label: discipline,
                            })),
                          ]}
                        />
                      )}
                      errors={errors}
                    />
                  ) : (
                    ""
                  )}
                </Row>
              </Col>
            </Row>
            <Row>
              <div style={{ fontSize: 16 }}>{formatMessage({ id: "colors" })}:</div>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <FormControl
                  labelText="colors.primary"
                  name="colors.primary"
                  labelTooltipId="primary"
                  control={control}
                  labelTooltip={formatMessage({
                    id: "form.help.team.colors.primary",
                  })}
                  render={({ field }) => (
                    <StyledColorPicker
                      format="hex"
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(color) => {
                        field.onChange(color.toHex());
                      }}
                    />
                  )}
                />
              </Col>
              <Col span={8}>
                <FormControl
                  labelText="colors.secondary"
                  name="colors.secondary"
                  labelTooltipId="secondary"
                  control={control}
                  labelTooltip={formatMessage({
                    id: "form.help.team.colors.secondary",
                  })}
                  render={({ field }) => (
                    <StyledColorPicker
                      format="hex"
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(color) => {
                        field.onChange(color.toHex());
                      }}
                    />
                  )}
                />
              </Col>
              <Col span={8}>
                <FormControl
                  labelText="colors.tertiary"
                  name="colors.tertiary"
                  control={control}
                  labelTooltipId="tertiary"
                  labelTooltip={formatMessage({
                    id: "form.help.team.colors.tertiary",
                  })}
                  render={({ field }) => (
                    <StyledColorPicker
                      format="hex"
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(color) => {
                        field.onChange(color.toHex());
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <div style={{ fontSize: 16 }}>{formatMessage({ id: "social" })}:</div>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <FormControl
                  labelText="website"
                  name="social.website"
                  labelTooltipId="website"
                  control={control}
                  labelTooltip={formatMessage(
                    { id: "form.help.item.website" },
                    { item: formatMessage({ id: "team" }) },
                  )}
                  render={({ field }) => <StyledFormInputs {...field} />}
                />
                <FormControl
                  labelText="facebook"
                  name="social.facebook"
                  labelTooltipId="facebook"
                  control={control}
                  labelTooltip={formatMessage(
                    { id: "form.help.item.facebook" },
                    { item: formatMessage({ id: "team" }) },
                  )}
                  render={({ field }) => <StyledFormInputs {...field} />}
                />
              </Col>
              <Col span={12}>
                <FormControl
                  labelText="twitter"
                  name="social.twitter"
                  labelTooltipId="twitter"
                  control={control}
                  labelTooltip={formatMessage(
                    { id: "form.help.item.twitter" },
                    { item: formatMessage({ id: "team" }) },
                  )}
                  render={({ field }) => <StyledFormInputs {...field} />}
                />
                <FormControl
                  labelText="instagram"
                  name="social.instagram"
                  labelTooltipId="instagram"
                  control={control}
                  labelTooltip={formatMessage(
                    { id: "form.help.item.instagram" },
                    { item: formatMessage({ id: "team" }) },
                  )}
                  render={({ field }) => <StyledFormInputs {...field} />}
                />
              </Col>
            </Row>
            {entityId && (
              <Row gutter={16}>
                <Col span={12}>
                  <Space>
                    {formatMessage({
                      id: "created_at",
                      defaultMessage: "Created At",
                    })}
                    <i className="fas fa-xs fa-lock" />
                  </Space>
                  <Input style={{ color: "black" }} disabled value={dayjs(data?.added).format("DD/MM/YYYY")} />
                </Col>
                <Col span={12}>
                  <Space>
                    {formatMessage({
                      id: "updated_at",
                      defaultMessage: "Updated At",
                    })}
                    <i className="fas fa-xs fa-lock" />
                  </Space>
                  <Input style={{ color: "black" }} disabled value={dayjs(data?.updated).format("DD/MM/YYYY")} />
                </Col>
              </Row>
            )}
            <Row style={{ marginTop: 20 }}>
              <PermissionCheck
                permissionKeys={[PermissionName as PermissionNames]}
                permissionOptions={entityId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
              >
                <Button htmlType="submit" type="primary">
                  {entityId ? formatMessage({ id: "team.update" }) : formatMessage({ id: "team.add" })}
                </Button>
              </PermissionCheck>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/entities`}>
                <Button style={{ marginLeft: 30 }} type="primary">
                  {formatMessage({ id: "close" })}
                </Button>
              </Link>
            </Row>
          </form>
          <RightColumn>
            {data?.entityId && (
              <>
                <UploadImageForm
                  imageType={ImageTypes.Logo}
                  baseId={data.entityId}
                  initialImage={data.images?.find((image) => image?.imageType === ImageTypes.Logo)}
                  baseType={ImageBaseTypes.Entity}
                  permissionKey={PermissionName as PermissionNames}
                />
                <HistoricNames team={data} fetchData={refetchEntity} permissionName={PermissionName} />
              </>
            )}
          </RightColumn>
        </Content>
      </LoaderContainer>
    </Page>
  );
};

const Content = styled.div`
    display: flex;

    .container {
        margin: 0;
    }
`;

const RightColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-left: 16px;
`;
export default ManageTeam;
